export type SearchableKeyword = {
  search: string;
  search_volume?: number | null;
  hero_relevance?: number | null;
};

/** Strips a wrapping pair of double quotes, if present, from a trimmed search string. */
export function stripQuotes(trimmedValue: string): string {
  if (trimmedValue.length >= 2 && trimmedValue.startsWith('"') && trimmedValue.endsWith('"')) {
    return trimmedValue.slice(1, -1).trim();
  }
  return trimmedValue;
}

/**
 * Filters keywords by a search query for autocomplete/picker UIs. The exact
 * match (if any) always sorts first; every other substring match is ranked
 * by relevance then search volume — not by match position (prefix vs.
 * mid-string) — so a single high-quality match doesn't get buried behind a
 * pile of low-quality prefix matches, and a real match never gets silently
 * cut off by `limit` just because weaker matches happen to sort earlier in
 * the source list.
 *
 * Wrapping the query in quotes ("exact phrase") switches to an exact-match
 * filter instead of substring matching.
 */
export function searchKeywords<T extends SearchableKeyword>(
  keywords: T[],
  rawQuery: string,
  limit?: number,
): T[] {
  const trimmed = rawQuery.trim();
  if (!trimmed) return [];

  const isQuoted = trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"');
  const query = stripQuotes(trimmed).toLowerCase();
  if (!query) return [];

  if (isQuoted) {
    const exact = keywords.filter((k) => k.search.toLowerCase() === query);
    return limit != null ? exact.slice(0, limit) : exact;
  }

  const byQuality = (a: T, b: T) => {
    const relDiff = (b.hero_relevance ?? -1) - (a.hero_relevance ?? -1);
    if (relDiff !== 0) return relDiff;
    return (b.search_volume ?? 0) - (a.search_volume ?? 0);
  };

  const exact: T[] = [];
  const rest: T[] = [];
  for (const k of keywords) {
    const s = k.search.toLowerCase();
    if (s === query) exact.push(k);
    else if (s.includes(query)) rest.push(k);
  }
  exact.sort(byQuality);
  rest.sort(byQuality);

  const ranked = [...exact, ...rest];
  return limit != null ? ranked.slice(0, limit) : ranked;
}
